import { useState, useEffect } from "react"
import { cloneDeep } from "lodash"
import { useShallow } from "zustand/react/shallow"

// components
import Modal from "components/Modal/Modal"
import Checkbox from "components/Checkbox/Checkbox"

// context
import useResultsTableViewStore from "../../../../hooks/stores/useResultsTableViewStore"
import useAuthoringDataStore from "../../../../hooks/stores/useAuthoringDataStore"
import useAuthoringViewStore from "../../../../hooks/stores/useAuthoringViewStore"

// utils
import { isArmTitleAndDescEqual } from "../../utils"

// styles
import "../ArmOverrideModal/ArmOverrideModal.scss"

const defaultValues = {
  "participant-flow": {
    ctg: {
      pf: true,
      bc: false,
      om: false,
      ae: false,
    },
    eudract: {
      pf: true,
      bc: true,
      om: true,
      ae: false,
    },
  },
  "baseline-characteristics": {
    ctg: {
      pf: false,
      bc: true,
      om: false,
      ae: false,
    },
    eudract: {
      pf: true,
      bc: true,
      om: true,
      ae: false,
    },
  },
  "outcome-measures": {
    ctg: {
      pf: false,
      bc: false,
      om: true,
      ae: false,
    },
    eudract: {
      pf: true,
      bc: true,
      om: true,
      ae: false,
    },
  },
  "adverse-events": {
    ctg: {
      pf: false,
      bc: false,
      om: false,
      ae: true,
    },
    eudract: {
      pf: false,
      bc: false,
      om: false,
      ae: true,
    },
  },
}

const registries = ["ctg", "eudract"]

const INITIAL_SOURCE = {
  "participant-flow": [
    {
      registry: "ctg",
      label: "CTG",
      items: [
        {
          disabled: true,
          label: "Participant flow",
          key: "pf",
        },
        {
          disabled: true,
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          disabled: true,
          label: "Outcome measures",
          key: "om",
        },
        {
          disabled: true,
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
    {
      registry: "eudract",
      label: "EudraCT",
      items: [
        {
          disabled: true,
          label: "Subject disposition",
          key: "pf",
        },
        {
          disabled: true,
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          disabled: true,
          label: "Outcome measures",
          key: "om",
        },
        {
          disabled: true,
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
  ],
  "baseline-characteristics": [
    {
      registry: "ctg",
      label: "CTG",
      items: [
        {
          disabled: true,
          label: "Participant flow",
          key: "pf",
        },
        {
          disabled: true,
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          disabled: true,
          label: "Outcome measures",
          key: "om",
        },
        {
          disabled: true,
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
    {
      registry: "eudract",
      label: "EudraCT",
      items: [
        {
          disabled: true,
          label: "Subject disposition",
          key: "pf",
        },
        {
          disabled: true,
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          disabled: true,
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
  ],
  "outcome-measures": [
    {
      registry: "ctg",
      label: "CTG",
      items: [
        {
          disabled: true,
          label: "Participant flow",
          key: "pf",
        },
        {
          label: "Baseline characteristics",
          key: "bc",
          disabled: true,
        },
        {
          label: "Outcome measures",
          key: "om",
          disabled: true,
        },
        {
          label: "Adverse events",
          key: "ae",
          disabled: true,
        },
      ],
    },
    {
      registry: "eudract",
      label: "EudraCT",
      items: [
        {
          disabled: true,
          label: "Subject disposition",
          key: "pf",
        },
        {
          disabled: true,
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          disabled: true,
          label: "Outcome measures",
          key: "om",
        },
        {
          disabled: true,
          label: "Adverse events",
          key: "ae",
        },
      ],
    },
  ],
  "adverse-events": [
    {
      registry: "ctg",
      label: "CTG",
      items: [
        {
          label: "Participant flow",
          key: "pf",
          disabled: true,
        },
        {
          label: "Baseline characteristics",
          key: "bc",
          disabled: true,
        },
        {
          label: "Outcome measures",
          key: "om",
          disabled: true,
        },
        {
          label: "Adverse events",
          key: "ae",
          disabled: true,
        },
      ],
    },
    {
      registry: "eudract",
      label: "EudraCT",
      items: [
        {
          label: "Subject disposition",
          key: "pf",
          disabled: true,
        },
        {
          disabled: true,
          label: "Baseline characteristics",
          key: "bc",
        },
        {
          disabled: true,
          label: "Outcome measures",
          key: "om",
        },
        {
          label: "Adverse events",
          key: "ae",
          disabled: true,
        },
      ],
    },
  ],
}

const stripId = (id) => {
  if (!id) return id
  const arr = id.split(".")
  return arr[arr.length - 1]
}

export const bcMoveLeftHelper = (tempData, registry, key, i) => {
  let tempKeyData = [...tempData[registry][key]]
  const prevIndex = i - 1
  let saved = cloneDeep(tempKeyData[prevIndex])
  if (saved) {
    tempKeyData[prevIndex] = tempKeyData[i]
    tempKeyData[i] = saved
    tempData[registry][key] = [...tempKeyData]
  }
}

export const bcMoveRightHelper = (tempData, registry, key, i) => {
  let tempKeyData = [...tempData[registry][key]]
  const nextIndex = i + 1
  let saved = cloneDeep(tempKeyData[nextIndex])
  if (saved) {
    tempKeyData[nextIndex] = tempKeyData[i]
    tempKeyData[i] = saved
    tempData[registry][key] = [...tempKeyData]
  }
}

export const omMoveLeftHelper = (om, key, i) => {
  let tempGroups = [...om[key]]
  const prevIndex = i - 1
  let saved = cloneDeep(tempGroups[prevIndex])
  if (saved) {
    tempGroups[prevIndex] = tempGroups[i]
    tempGroups[i] = saved
    om[key] = [...tempGroups]
  }
}

export const omMoveRightHelper = (om, key, i) => {
  let tempGroups = [...om[key]]
  const nextIndex = i + 1
  let saved = cloneDeep(tempGroups[nextIndex])
  if (saved) {
    tempGroups[nextIndex] = tempGroups[i]
    tempGroups[i] = saved
    om[key] = [...tempGroups]
  }
}

export const aeMoveLeftHelper = (array, i) => {
  const prevIndex = i - 1
  let saved = cloneDeep(array[prevIndex])
  if (saved) {
    array[prevIndex] = array[i]
    array[i] = saved
  }
}

export const aeMoveRightHelper = (array, i) => {
  const nextIndex = i + 1
  let saved = cloneDeep(array[nextIndex])
  if (saved) {
    array[nextIndex] = array[i]
    array[i] = saved
  }
}

const eudractTableSet = new Set(["participant-flow", "baseline-characteristics", "outcome-measures"])

const ArmReorderModal = ({ arm, source, open, onCancel, direction, setDirection, colIndex, periodIndex }) => {
  const { formData, setFormData } = useAuthoringDataStore(
    useShallow((state) => ({
      formData: state.formData,
      setFormData: state.setFormData,
    }))
  )
  const [loading, setLoading] = useState(false)

  const { refreshFormData, setRefreshFormData } = useAuthoringViewStore(
    useShallow((state) => ({
      refreshFormData: state.refreshFormData,
      setRefreshFormData: state.setRefreshFormData,
    }))
  )

  const [applicableSections, setApplicableSections] = useState({
    eudract: {
      pf: false,
      bc: false,
      om: false,
      ae: false,
    },
    ctg: {
      pf: false,
      bc: false,
      om: false,
      ae: false,
    },
  })
  const [sources, setSources] = useState({})
  const { tableView } = useResultsTableViewStore()

  useEffect(() => {
    setApplicableSections(defaultValues[source])
  }, [defaultValues])

  useEffect(() => {
    if (open) {
      getSourceAndCompareArms()
    } else {
      setSources({})
    }
  }, [open])

  const getSourceAndCompareArms = () => {
    switch (source) {
      case "participant-flow":
        let pfTable = formData?.results?.participant_flow?.participant_flow_table
        let pfArms = pfTable?.["global_arms"] || []
        if (tableView === "eudract") {
          pfArms = pfTable["eudract_periods"][periodIndex]?.arms
        }
        compareArms(pfArms)
        break
      case "baseline-characteristics":
        let bcArms = formData?.results?.baseline_characteristics?.baseline_characteristics_table?.[tableView]?.arms
        compareArms(bcArms)
        break
      case "outcome-measures":
        let omTable = formData?.results?.outcome_measures?.outcome_measures_table
        let omArms = omTable?.[tableView]?.outcome_measures?.[periodIndex]?.arms
        compareArms(omArms)
        break
      case "adverse-events":
        let ae = formData?.results?.adverse_events?.adverse_events_table
        let aeArms = ae?.[tableView]?.groups
        compareArms(aeArms)
        break
      default:
    }
  }

  const isEqualAndOrdered = (a = [], b = []) => {
    if (a.length !== b.length) return false

    for (let i = 0; i < a.length; i++) {
      const { title: titleA, description: descA } = a[i]
      const { title: titleB, description: descB } = b[i]

      if (titleA !== titleB || descA !== descB) return false
    }

    return true
  }

  const compareArms = (original = []) => {
    let tempData = cloneDeep(INITIAL_SOURCE)
    registries.forEach((registry) => {
      let pfGlobalArms = formData?.results?.participant_flow?.participant_flow_table?.global_arms || []
      let bcArms = formData?.results?.baseline_characteristics?.baseline_characteristics_table?.[registry]?.arms || []
      let aeArms = formData?.results?.adverse_events?.adverse_events_table?.[registry]?.groups || []
      let oms = formData?.results?.outcome_measures?.outcome_measures_table?.[registry]?.outcome_measures || []
      let eudractPeriods = formData?.results?.participant_flow?.participant_flow_table?.eudract_periods || []

      let originalArmTitleAndDesc = original.map((arm) => {
        return {
          title: arm.title,
          description: arm.description,
        }
      })

      let pfGlobalArmsTitleAndDesc = pfGlobalArms?.map((arm) => {
        return {
          title: arm.title,
          description: arm.description,
        }
      })

      let bcArmTitleAndDesc = bcArms?.map((arm) => {
        return {
          title: arm.title,
          description: arm.description,
        }
      })

      let aeArmTitleAndDesc = aeArms?.map((arm) => {
        return {
          title: arm.title,
          description: arm.description,
        }
      })

      if (source !== "participant-flow") {
        if (registry === "ctg") {
          if (isEqualAndOrdered(originalArmTitleAndDesc, pfGlobalArmsTitleAndDesc)) {
            enableTable(tempData, source, "pf", registry)
          }
        } else if (registry === "eudract" && eudractPeriods && !eudractTableSet.has(source)) {
          for (let period of eudractPeriods) {
            let periodArmsTitleAndDesc = period.arms.map((arm) => {
              return {
                title: arm.title,
                description: arm.description,
              }
            })

            if (isEqualAndOrdered(originalArmTitleAndDesc, periodArmsTitleAndDesc)) {
              enableTable(tempData, source, "pf", registry)
              break
            }
          }
        }
      }

      const bcBool = isEqualAndOrdered(originalArmTitleAndDesc, bcArmTitleAndDesc)

      if (isEqualAndOrdered(originalArmTitleAndDesc, bcArmTitleAndDesc) && source !== "baseline-characteristics") {
        if (registry !== "eudract" || !eudractTableSet.has(source)) {
          enableTable(tempData, source, "bc", registry)
        }
      }

      if (oms && source !== "outcome-measures") {
        if (registry !== "eudract" || !eudractTableSet.has(source)) {
          for (let om of oms) {
            let omArmsTitleAndDesc = om.arms.map((arm) => {
              return {
                title: arm.title,
                description: arm.description,
              }
            })

            if (isEqualAndOrdered(originalArmTitleAndDesc, omArmsTitleAndDesc)) {
              enableTable(tempData, source, "om", registry)
              break
            }
          }
        }
      }

      if (isEqualAndOrdered(originalArmTitleAndDesc, aeArmTitleAndDesc) && source !== "adverse-events") {
        enableTable(tempData, source, "ae", registry)
      } else {
        disableTable(tempData, source, "ae", registry)
        if (isEqualAndOrdered(originalArmTitleAndDesc, aeArmTitleAndDesc) !== true) {
          setApplicableSections({
            ...applicableSections,
            [registry]: {
              ...applicableSections[registry],
              ae: false,
            },
          })
        }
      }
    })

    setSources(tempData)
  }

  const enableTable = (tempData, source, key, registry) => {
    let foundRegIndex = tempData[source].findIndex((item) => item.registry === registry)

    if (foundRegIndex !== -1) {
      let foundTableIndex = tempData[source][foundRegIndex].items.findIndex((table) => table.key === key)

      if (foundTableIndex !== -1) {
        tempData[source][foundRegIndex].items[foundTableIndex].disabled = false
      }
    }
  }

  const disableTable = (tempData, source, key, registry) => {
    let foundRegIndex = tempData[source].findIndex((item) => item.registry === registry)

    if (foundRegIndex !== -1) {
      let foundTableIndex = tempData[source][foundRegIndex].items.findIndex((table) => table.key === key)

      if (foundTableIndex !== -1) {
        tempData[source][foundRegIndex].items[foundTableIndex].disabled = true
      }
    }
  }

  const onSave = () => {
    setLoading(true)
    let tempData = { ...formData }

    Object.keys(applicableSections).map((registry) => {
      let sections = applicableSections[registry]
      if (sections.pf) {
        findPfArmAndSave(tempData, registry, arm)
      }
      if (sections.bc) {
        findBcArmAndSave(tempData, registry, arm)
      }
      if (sections.om) {
        findOmArmAndSave(tempData, registry, arm)
      }
      if (sections.ae) {
        findAeArmAndSave(tempData, registry, arm)
      }
    })

    setFormData(tempData)
    setTimeout(() => {
      onCancel()
      setRefreshFormData(!refreshFormData)
      setLoading(false)
      setDirection(null)
      setApplicableSections(defaultValues[source])
    }, 500)
  }

  const findPfArmAndSave = (data, registry, ogArm) => {
    let pfTable = data?.results?.participant_flow?.participant_flow_table

    if (direction === "right") {
      if (registry === "ctg" && pfTable?.["global_arms"]) {
        let global_arms = [...pfTable.global_arms]
        const nextIndex = colIndex + 1
        let saved = cloneDeep(global_arms[nextIndex])
        if (saved) {
          global_arms[nextIndex] = global_arms[colIndex]
          global_arms[colIndex] = saved

          let ctgPeriods = [...pfTable.ctg_periods]
          ctgPeriods.forEach((period) => {
            let milestones = [...period.milestone_list]
            milestones.forEach((milestone) => {
              let participants = [...milestone.participants]
              let saved = participants[nextIndex]
              participants[nextIndex] = participants[colIndex]
              participants[colIndex] = saved

              milestone.participants = participants
            })

            let withdrawnList = [...period.drop_withdrawn_reason_list]
            withdrawnList.forEach((withdrawn) => {
              let participants = [...withdrawn.participants]
              let saved = participants[nextIndex]
              participants[nextIndex] = participants[colIndex]
              participants[colIndex] = saved

              withdrawn.participants = participants
            })

            period.drop_withdrawn_reason_list = withdrawnList
            period.milestone_list = milestones
          })

          data.results.participant_flow.participant_flow_table.ctg_periods = ctgPeriods
          data.results.participant_flow.participant_flow_table.global_arms = global_arms
        }
      } else if (registry === "eudract" && pfTable?.["eudract_periods"]) {
        pfTable["eudract_periods"].forEach((period, periodIndex) => {
          if (
            stripId(period?.arms?.[colIndex]?.id) === stripId(ogArm.id) ||
            isArmTitleAndDescEqual(period?.arms?.[colIndex], ogArm)
          ) {
            let periodArms = [...period.arms]
            const nextIndex = colIndex + 1
            let saved = cloneDeep(periodArms[nextIndex])
            if (saved) {
              periodArms[nextIndex] = periodArms[colIndex]
              periodArms[colIndex] = saved

              let euPeriods = [...pfTable.eudract_periods]
              let euPeriod = euPeriods[periodIndex]
              euPeriod.arms = [...periodArms]

              let milestones = [...euPeriod.milestone_list]
              milestones.forEach((milestone) => {
                let participants = [...milestone.participants]
                let saved = participants[nextIndex]
                participants[nextIndex] = participants[colIndex]
                participants[colIndex] = saved

                milestone.participants = participants
              })

              let withdrawnList = [...euPeriod.drop_withdrawn_reason_list]
              withdrawnList.forEach((withdrawn) => {
                let participants = [...withdrawn.participants]
                let saved = participants[nextIndex]
                participants[nextIndex] = participants[colIndex]
                participants[colIndex] = saved

                withdrawn.participants = participants
              })

              euPeriod.drop_withdrawn_reason_list = withdrawnList
              euPeriod.milestone_list = milestones

              euPeriods.splice(periodIndex, 1, euPeriod)
              pfTable.eudract_periods = euPeriods
            }
          }
        })
      }
    } else if (direction === "left") {
      if (registry === "ctg" && pfTable?.["global_arms"]) {
        if (
          stripId(pfTable?.["global_arms"]?.[colIndex]?.id) === stripId(ogArm.id) ||
          isArmTitleAndDescEqual(pfTable?.["global_arms"]?.[colIndex], ogArm)
        ) {
          let global_arms = [...pfTable.global_arms]
          const prevIndex = colIndex - 1
          let saved = cloneDeep(global_arms[prevIndex])
          if (saved) {
            global_arms[prevIndex] = global_arms[colIndex]
            global_arms[colIndex] = saved

            let ctgPeriods = [...pfTable.ctg_periods]
            ctgPeriods.forEach((period) => {
              let milestones = [...period.milestone_list]
              milestones.forEach((milestone) => {
                let participants = [...milestone.participants]
                let saved = participants[prevIndex]
                participants[prevIndex] = participants[colIndex]
                participants[colIndex] = saved

                milestone.participants = participants
              })

              let withdrawnList = [...period.drop_withdrawn_reason_list]
              withdrawnList.forEach((withdrawn) => {
                let participants = [...withdrawn.participants]
                let saved = participants[prevIndex]
                participants[prevIndex] = participants[colIndex]
                participants[colIndex] = saved

                withdrawn.participants = participants
              })

              period.drop_withdrawn_reason_list = withdrawnList
              period.milestone_list = milestones
            })

            pfTable.ctg_periods = ctgPeriods
            pfTable.global_arms = global_arms
          }
        }
      } else if (registry === "eudract" && pfTable?.["eudract_periods"]) {
        pfTable["eudract_periods"].forEach((period, periodIndex) => {
          if (
            (period.arms && stripId(period.arms?.[colIndex]?.id) === stripId(ogArm.id)) ||
            isArmTitleAndDescEqual(period.arms?.[colIndex], ogArm)
          ) {
            let periodArms = [...period.arms]

            const prevIndex = colIndex - 1
            let saved = cloneDeep(periodArms[prevIndex])
            if (saved) {
              periodArms[prevIndex] = periodArms[colIndex]
              periodArms[colIndex] = saved

              let euPeriods = [...pfTable.eudract_periods]
              let euPeriod = euPeriods[periodIndex]
              euPeriod.arms = [...periodArms]

              let milestones = [...euPeriod.milestone_list]
              milestones.forEach((milestone) => {
                let participants = [...milestone.participants]
                let saved = participants[prevIndex]
                participants[prevIndex] = participants[colIndex]
                participants[colIndex] = saved

                milestone.participants = participants
              })

              let withdrawnList = [...euPeriod.drop_withdrawn_reason_list]
              withdrawnList.forEach((withdrawn) => {
                let participants = [...withdrawn.participants]
                let saved = participants[prevIndex]
                participants[prevIndex] = participants[colIndex]
                participants[colIndex] = saved

                withdrawn.participants = participants
              })

              euPeriod.drop_withdrawn_reason_list = withdrawnList
              euPeriod.milestone_list = milestones

              euPeriods.splice(periodIndex, 1, euPeriod)
              pfTable.eudract_periods = euPeriods
            }
          }
        })
      }
    }
  }

  const findBcArmAndSave = (data, registry, ogArm) => {
    let bcTable = data?.results?.baseline_characteristics?.baseline_characteristics_table
    let regData = bcTable?.[registry]
    let arms = regData?.arms
    let arm = arms?.[colIndex]
    let sas = regData?.subject_analysis_sets?.[colIndex]

    if (
      stripId(arm?.id) === stripId(ogArm.id) ||
      stripId(sas?.id) === stripId(ogArm.id) ||
      isArmTitleAndDescEqual(arm, ogArm) ||
      isArmTitleAndDescEqual(sas, ogArm)
    ) {
      const arrKey =
        stripId(arm?.id) === stripId(ogArm.id) || isArmTitleAndDescEqual(arm, ogArm) ? "arms" : "subject_analysis_sets"
      const group = stripId(arm?.id) === stripId(ogArm.id) || isArmTitleAndDescEqual(arm, ogArm) ? arm : sas

      if (direction === "right") {
        bcMoveRightHelper(bcTable, registry, arrKey, colIndex)

        // move baseline measure values
        bcTable[registry]?.baseline_measures?.forEach((bm) => {
          bm.rows.forEach((row) => {
            row.categories.forEach((category) => {
              let tempValues = [...category.values]
              let colIndex = category.values.findIndex((item) => stripId(item?.arm_id) === stripId(group?.id))

              if (colIndex !== -1 && colIndex !== category.values.length - 1) {
                const nextIndex = colIndex + 1
                let saved = cloneDeep(tempValues[nextIndex])
                if (saved) {
                  tempValues[nextIndex] = tempValues[colIndex]
                  tempValues[colIndex] = saved
                  category.values = [...tempValues]
                }
              }
            })
          })
        })
      } else if (direction === "left") {
        // move arms
        bcMoveLeftHelper(bcTable, registry, arrKey, colIndex)

        // move baseline measure values
        bcTable[registry]?.baseline_measures?.forEach((bm) => {
          bm.rows.forEach((row) => {
            row.categories.forEach((category) => {
              let tempValues = [...category.values]
              let colIndex = tempValues.findIndex((item) => stripId(item?.arm_id) === stripId(group?.id))

              if (colIndex !== -1 && colIndex !== 0) {
                const prevIndex = colIndex - 1
                let saved = cloneDeep(tempValues[prevIndex])
                if (saved) {
                  tempValues[prevIndex] = tempValues[colIndex]
                  tempValues[colIndex] = saved
                  category.values = [...tempValues]
                }
              }
            })
          })
        })
      }
    }
  }

  const findOmArmAndSave = (data, registry, ogArm) => {
    let omTable = data?.results?.outcome_measures?.outcome_measures_table
    let regData = omTable?.[registry]

    if (direction === "right") {
      regData.outcome_measures.forEach((om) => {
        let arm = om?.arms?.[colIndex]
        let sas = om?.subject_analysis_sets?.[colIndex]

        if (
          stripId(arm?.id) === stripId(ogArm.id) ||
          stripId(sas?.id) === stripId(ogArm.id) ||
          isArmTitleAndDescEqual(arm, ogArm) ||
          isArmTitleAndDescEqual(sas, ogArm)
        ) {
          const arrKey =
            stripId(arm?.id) === stripId(ogArm.id) || isArmTitleAndDescEqual(arm, ogArm)
              ? "arms"
              : "subject_analysis_sets"
          const group = stripId(arm?.id) === stripId(ogArm.id) || isArmTitleAndDescEqual(arm, ogArm) ? arm : sas

          omMoveRightHelper(om, arrKey, colIndex)
          om.measure_rows.forEach((row) => {
            row.categories.forEach((category) => {
              let tempValues = [...category.values]

              let colIndex = tempValues.findIndex((item) => stripId(item?.group_id) === stripId(group?.id))

              if (colIndex !== -1 && colIndex !== category.values.length - 1) {
                const nextIndex = colIndex + 1
                let saved = cloneDeep(tempValues[nextIndex])
                tempValues[nextIndex] = tempValues[colIndex]
                tempValues[colIndex] = saved
                category.values = [...tempValues]
              }
            })
          })
        }
      })
    } else if (direction === "left" && regData?.outcome_measures) {
      regData.outcome_measures.forEach((om) => {
        let arm = om?.arms?.[colIndex]
        let sas = om?.subject_analysis_sets?.[colIndex]

        if (
          stripId(arm?.id) === stripId(ogArm.id) ||
          stripId(sas?.id) === stripId(ogArm.id) ||
          isArmTitleAndDescEqual(arm, ogArm) ||
          isArmTitleAndDescEqual(sas, ogArm)
        ) {
          const arrKey =
            stripId(arm?.id) === stripId(ogArm.id) || isArmTitleAndDescEqual(arm, ogArm)
              ? "arms"
              : "subject_analysis_sets"
          const group = stripId(arm?.id) === stripId(ogArm.id) || isArmTitleAndDescEqual(arm, ogArm) ? arm : sas

          omMoveLeftHelper(om, arrKey, colIndex)
          om.measure_rows.forEach((row) => {
            row.categories.forEach((category) => {
              let tempValues = [...category.values]
              let colIndex = tempValues.findIndex((item) => stripId(item?.group_id) === stripId(group?.id))

              if (colIndex !== -1 && colIndex !== 0) {
                const prevIndex = colIndex - 1
                let saved = cloneDeep(tempValues[prevIndex])
                if (saved) {
                  tempValues[prevIndex] = tempValues[colIndex]
                  tempValues[colIndex] = saved
                  category.values = [...tempValues]
                }
              }
            })
          })
        }
      })
    }
  }

  const findAeArmAndSave = (data, registry, ogArm) => {
    let ae = data?.results?.adverse_events?.adverse_events_table
    if (
      stripId(ae?.[registry]?.groups[colIndex]?.id) === stripId(ogArm.id) ||
      isArmTitleAndDescEqual(ae?.[registry]?.groups[colIndex], ogArm)
    ) {
      if (direction === "right") {
        // move serious adverse events
        ae[registry].serious_adverse_events.forEach((event) => {
          if (colIndex !== -1 && colIndex !== event.values.length - 1) aeMoveRightHelper(event.values, colIndex)
        })

        // move non serious adverse events
        ae[registry].non_serious_adverse_events.forEach((event) => {
          if (colIndex !== -1 && colIndex !== event.values.length - 1) aeMoveRightHelper(event.values, colIndex)
        })

        // move arm
        if (colIndex !== -1 && colIndex !== ae[registry].groups.length - 1)
          aeMoveRightHelper(ae[registry].groups, colIndex)
      } else if (direction === "left") {
        ae[registry].serious_adverse_events.forEach((event) => {
          if (colIndex !== -1 && colIndex !== 0) aeMoveLeftHelper(event.values, colIndex)
        })

        // move non serious adverse events
        ae[registry].non_serious_adverse_events.forEach((event) => {
          if (colIndex !== -1 && colIndex !== 0) aeMoveLeftHelper(event.values, colIndex)
        })

        // move arm
        if (colIndex !== -1 && colIndex !== 0) aeMoveLeftHelper(ae[registry].groups, colIndex)
      }
    }
  }

  const onCheck = (key, value, registry) => {
    if ((key === "pf" || key === "bc" || key === "om") && registry === "eudract") {
      setApplicableSections({
        ...applicableSections,
        [registry]: {
          ...applicableSections[registry],
          pf: value,
          bc: value,
          om: value,
        },
      })
    } else {
      setApplicableSections({
        ...applicableSections,
        [registry]: {
          ...applicableSections[registry],
          [key]: value,
        },
      })
    }
  }

  return (
    <Modal
      title={`Move arm/group: ${arm?.title}`}
      open={open}
      onCancel={onCancel}
      hideImage={true}
      destroyOnClose={true}
      width={500}
      onOk={onSave}
      confirmLoading={loading}
      okText={"Save changes"}
    >
      <div className="arm-override-modal-container">
        <div className="registries-container">
          {sources?.[source]?.map((registry) => {
            return (
              <div
                key={registry.registry}
                className="registry-container"
              >
                <p>{registry.label}</p>
                <div>
                  {registry.items.map((checkbox) => {
                    return (
                      <Checkbox
                        label={checkbox.label}
                        key={registry.registry + checkbox.key}
                        disabled={checkbox.disabled}
                        checked={applicableSections?.[registry.registry]?.[checkbox.key]}
                        onChange={(e) => onCheck(checkbox.key, e.target.checked, registry.registry)}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </Modal>
  )
}

export default ArmReorderModal
